import { useI18n } from 'vue-i18n'

import type { Validation } from '@vuelidate/core'
import type { Ref } from 'vue'

type FormValidationStatus = 'error' | 'warning' | 'success'

interface ValidatorDisplayInfo {
  validationStatus: (field: string) => FormValidationStatus
  msg: (field: string) => string
}

type Validators = {
  required: ValidatorDisplayInfo
  maxLength: ValidatorDisplayInfo
  minLength: ValidatorDisplayInfo
  minValue: ValidatorDisplayInfo
  maxValue: ValidatorDisplayInfo
  unique: ValidatorDisplayInfo
}

/**
 * Options for validators.
 */
interface ValidatorOptions {
  /**
   * Options for the required validator.
   * Use either `label` or `msg`, but not both.
   */
  required?:
    | {
        label?: never // If msg is provided, label should not be provided
        msg: string // Error message for the required validation
        validationStatus?: FormValidationStatus // Optional validation status
      }
    | {
        label: string // Label for the required field
        msg?: never // If label is provided, msg should not be provided
        validationStatus?: FormValidationStatus // Optional validation status
      }
  /**
   * Options for the maxLength validator.
   * Use either `label` and `value`, or `msg`, but not both.
   */
  maxLength?:
    | {
        label?: never // If msg is provided, label and value should not be provided
        value?: never // If msg is provided, label and value should not be provided
        msg: string // Error message for the maxLength validation
        validationStatus?: FormValidationStatus // Optional validation status
      }
    | {
        label: string // Label for the maxLength field
        value: number // Maximum length allowed
        msg?: never // If label and value are provided, msg should not be provided
        validationStatus?: FormValidationStatus // Optional validation status
      }
  /**
   * Options for the maxLength validator.
   * Use either `label` and `value`, or `msg`, but not both.
   */
  minLength?:
    | {
        label?: never // If msg is provided, label and value should not be provided
        value?: never // If msg is provided, label and value should not be provided
        msg: string // Error message for the maxLength validation
        validationStatus?: FormValidationStatus // Optional validation status
      }
    | {
        label: string // Label for the maxLength field
        value: number // Maximum length allowed
        msg?: never // If label and value are provided, msg should not be provided
        validationStatus?: FormValidationStatus // Optional validation status
      }
  /**
   * Options for the minValue validator.
   * Use either `label` and `value`, or `msg`, but not both.
   */
  minValue?:
    | {
        label?: never // If msg is provided, label and value should not be provided
        value?: never // If msg is provided, label and value should not be provided
        msg: string // Error message for the maxLength validation
        validationStatus?: FormValidationStatus // Optional validation status
      }
    | {
        label: string // Label for the maxLength field
        value: number // Maximum length allowed
        msg?: never // If label and value are provided, msg should not be provided
        validationStatus?: FormValidationStatus // Optional validation status
      }
  /**
   * Options for the maxValue validator.
   * Use either `label` and `value`, or `msg`, but not both.
   */
  maxValue?:
    | {
        label?: never // If msg is provided, label and value should not be provided
        value?: never // If msg is provided, label and value should not be provided
        msg: string // Error message for the maxLength validation
        validationStatus?: FormValidationStatus // Optional validation status
      }
    | {
        label: string // Label for the maxLength field
        value: number // Maximum length allowed
        msg?: never // If label and value are provided, msg should not be provided
        validationStatus?: FormValidationStatus // Optional validation status
      }
  /**
   * Options for the unique validator.
   * Use either `label` or `msg`, but not both.
   */
  unique?:
    | {
        label?: never // If msg is provided, label should not be provided
        msg: string // Error message for the required validation
        validationStatus?: FormValidationStatus // Optional validation status
      }
    | {
        label: string // Label for the required field
        msg?: never // If label is provided, msg should not be provided
        validationStatus?: FormValidationStatus // Optional validation status
      }
}
interface ValidatorOptions {
  required?:
    | { label?: never; msg: string; validationStatus?: FormValidationStatus }
    | { label: string; msg?: never; validationStatus?: FormValidationStatus }
  maxLength?:
    | { label?: never; value?: never; msg: string; validationStatus?: FormValidationStatus }
    | { label: string; value: number; msg?: never; validationStatus?: FormValidationStatus }
  minLength?:
    | { label?: never; value?: never; msg: string; validationStatus?: FormValidationStatus }
    | { label: string; value: number; msg?: never; validationStatus?: FormValidationStatus }
  minValue?:
    | { label?: never; value?: never; msg: string; validationStatus?: FormValidationStatus }
    | { label: string; value: number; msg?: never; validationStatus?: FormValidationStatus }
  maxValue?:
    | { label?: never; value?: never; msg: string; validationStatus?: FormValidationStatus }
    | { label: string; value: number; msg?: never; validationStatus?: FormValidationStatus }
  unique?:
    | { label?: never; msg: string; validationStatus?: FormValidationStatus }
    | { label: string; msg?: never; validationStatus?: FormValidationStatus }
}

type FieldOptions<Form> = {
  [K in keyof Form]?: ValidatorOptions
}

/**
 * Bind validation options to a form.
 *
 * @template Form - The type of the form.
 * @param {Ref<Validation>} v - The validation reference.
 * @param {FieldOptions<Form>} [options] - The field options for validation.
 * @returns {object} - An object containing the `bindValidation` function.
 *
 * @example
 * Example 1:
 * Bind validation options for Form with label-based configuration using default validation message
 *
 * const { bindValidation } = useBindValidation<Form>(v, {
 *   name: { required: { label: labels.name } }, // Required validation with label
 *   account: { required: { label: labels.account } }, // Required validation with label
 *   status: { required: { label: labels.status } }, // Required validation with label
 *   tags: { maxLength: { label: labels.tags, value: 5 } }, // MaxLength validation with label and value
 * });

 * Example 2:
 * Bind validation options for Form with message-based configuration.
 *
 * const { bindValidation } = useBindValidation<Form>(v, {
 *   name: { required: { msg: requiredMsg('name') } }, // Required validation with message
 *   account: { required: { msg: requiredMsg('account') } }, // Required validation with message
 *   status: { required: { msg: requiredMsg('status') } }, // Required validation with message
 *   tags: {
 *     maxLength: {
 *       msg: t('canvasUI.CCreateTemplateModal.form.validation.maxLength', {
 *         field: labels.tags.toLowerCase(),
 *         value: TAGS_LIMIT,
 *       }),
 *     }, // MaxLength validation with message
 *   },
 * });
 */
export default <Form extends Record<string, any>>(v: Ref<Validation>, options?: FieldOptions<Form>) => {
  const { t } = useI18n()

  const validators: Validators = {
    required: {
      validationStatus: (field) => options?.[field]?.required?.validationStatus ?? 'error',
      msg: (field) =>
        options?.[field]?.required?.msg ??
        t('canvasUI.common.validation.required', {
          field: options?.[field]?.required?.label,
        }),
    },
    maxLength: {
      validationStatus: (field) => options?.[field]?.required?.validationStatus ?? 'error',
      msg: (field) =>
        options?.[field]?.maxLength?.msg ??
        t('canvasUI.common.validation.maxLength', {
          field: options?.[field]?.maxLength?.label?.toLowerCase(),
          value: options?.[field]?.maxLength?.value,
        }),
    },
    minLength: {
      validationStatus: (field) => options?.[field]?.required?.validationStatus ?? 'error',
      msg: (field) =>
        options?.[field]?.minLength?.msg ??
        t('canvasUI.common.validation.minLength', {
          field: options?.[field]?.minLength?.label?.toLowerCase(),
          value: options?.[field]?.minLength?.value,
        }),
    },
    minValue: {
      validationStatus: (field) => options?.[field]?.required?.validationStatus ?? 'error',
      msg: (field) =>
        options?.[field]?.minValue?.msg ??
        t('canvasUI.common.validation.minValue', {
          field: options?.[field]?.maxLength?.label?.toLowerCase(),
          value: options?.[field]?.maxLength?.value,
        }),
    },
    maxValue: {
      validationStatus: (field) => options?.[field]?.required?.validationStatus ?? 'error',
      msg: (field) =>
        options?.[field]?.maxValue?.msg ??
        t('canvasUI.common.validation.maxValue', {
          field: options?.[field]?.maxLength?.label?.toLowerCase(),
          value: options?.[field]?.maxLength?.value,
        }),
    },
    unique: {
      validationStatus: (field) => options?.[field]?.unique?.validationStatus ?? 'error',
      msg: (field) =>
        options?.[field]?.unique?.msg ??
        t('canvasUI.common.validation.unique', {
          field: options?.[field]?.unique?.label,
        }),
    },
  }

  /**
   * naive-ui bind validation for a specific field.
   *
   * @template Key - The key of the form field.
   * @param {Key} key - The key of the form field to bind validation for.
   * @returns {object} - An object containing validation information.
   */
  const bindValidation = <Key extends keyof Form>(key: Key) => {
    const vField = v.value[key as string]
    const validator = vField?.$errors[0]?.$validator as keyof Validators

    return {
      feedback: validators[validator]?.msg(key as string),
      showFeedback: vField?.$error,
      validationStatus: validators[validator]?.validationStatus(key as string),
    }
  }

  return { bindValidation }
}
